import type { BoardLinkKind } from "@/lib/board";
/**
 * Domain errors raised by service-layer adapters. These exist so the UI
 * can branch on `instanceof` (or `name`) without parsing error message
 * strings.
 */

/**
 * Thrown when an action requires a counterparty's wallet_address to be
 * synced into `profiles`, but the row's wallet_address is still NULL.
 *
 * Why this happens: a counterparty's profile row can exist with
 * wallet_address=NULL when they signed up before usePrivySync gated its
 * profile insert on the embedded wallet (legacy rows), or when their
 * Privy embedded wallet hasn't finished provisioning yet on their own
 * device. Either way, the current user's session can read the
 * counterparty's profile and find no address to lock funds against.
 *
 * Acceptance flows surface this so the UI can render a "waiting on
 * worker onboarding" state and re-enable the action once the wallet
 * syncs.
 */
export class WorkerWalletNotReadyError extends Error {
  readonly workerId: string;
  readonly workerName?: string;

  constructor(workerId: string, workerName?: string) {
    super(
      `Worker ${workerName ?? workerId} has not finished wallet onboarding yet`,
    );
    this.name = "WorkerWalletNotReadyError";
    this.workerId = workerId;
    this.workerName = workerName;
    // Preserve prototype chain across transpilation targets so
    // `error instanceof WorkerWalletNotReadyError` works in the UI.
    Object.setPrototypeOf(this, WorkerWalletNotReadyError.prototype);
  }
}

/**
 * Thrown when a worker's accept lost a race: another worker took the task (or
 * the listing's last slot) first. Nothing was changed on this worker's side.
 *
 * The database says this in several ways — the sibling-supersede flipping the
 * invitation to 'superseded', join_workspace_as_accepted_worker's "already
 * awarded", clone_quest_from_template's "Template is full / not open". The
 * adapter reads those messages so the UI does not have to: it branches on
 * `instanceof` and tells the worker "another volunteer just took this spot".
 * `message` keeps the underlying reason for logs.
 */
export class TaskAlreadyTakenError extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = "TaskAlreadyTakenError";
    Object.setPrototypeOf(this, TaskAlreadyTakenError.prototype);
  }
}

/**
 * Base for "the request may have been carried out, and we cannot tell".
 *
 * Distinct from a failure on purpose: a failure means the action did not
 * happen, so a retry is free. These mean nobody knows, so the UI must offer a
 * re-check rather than a confident "it failed" — and must not imply a retry is
 * consequence-free when the action sends email or spends money.
 *
 * Raised for a transport failure (no response at all) and for our own edge
 * proxy's 502/504, which mean it gave up on a worker that was still running.
 * See `isIndeterminate` in `@/lib/supabaseFunctionsError`.
 */
export class ActionUnconfirmedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ActionUnconfirmedError";
    Object.setPrototypeOf(this, ActionUnconfirmedError.prototype);
  }
}

/**
 * Thrown when an outreach send left the browser but no response came back, so
 * whether the email went out is genuinely unknown.
 *
 * Why this is its own error: supabase-js reports a transport failure as
 * `FunctionsFetchError`, whose message ("Failed to send a request to the Edge
 * Function") describes the transport and not the decision the admin has to
 * make. The request may have reached the worker and sent a real email, or never
 * left the machine at all — and a browser sharing one HTTP/2 connection for
 * REST and Functions can lose an in-flight send to an unrelated connection
 * reset, so this is not a rare path.
 *
 * Retrying is safe: the worker's send core requires status='approved' and
 * answers `not_approved` for a draft that already went out, so a second click
 * cannot double-send. The UI surfaces this as "may have been sent — re-check /
 * try again" rather than as a flat failure, and re-reads the draft row so the
 * real status decides.
 */
export class OutreachSendUnconfirmedError extends ActionUnconfirmedError {
  readonly draftId: string;

  constructor(draftId: string) {
    super(`Outreach send for draft ${draftId} got no response; outcome unknown`);
    this.name = "OutreachSendUnconfirmedError";
    this.draftId = draftId;
    // Preserve prototype chain across transpilation targets so
    // `error instanceof OutreachSendUnconfirmedError` works in the UI.
    Object.setPrototypeOf(this, OutreachSendUnconfirmedError.prototype);
  }
}

/**
 * Thrown when a re-enrich request got no usable answer, so whether the paid
 * resolve→enrich→verify chain ran is unknown.
 *
 * Money, not compliance, is what makes this worth distinguishing: the worker
 * keeps running after our proxy gives up, so an admin told "it failed" will
 * click again and buy the same lookup twice. The UI surfaces this as
 * "re-checking — reload before retrying" instead.
 */
export class ReenrichUnconfirmedError extends ActionUnconfirmedError {
  readonly candidateId: string;

  constructor(candidateId: string) {
    super(`Re-enrich for candidate ${candidateId} got no response; outcome unknown`);
    this.name = "ReenrichUnconfirmedError";
    this.candidateId = candidateId;
    Object.setPrototypeOf(this, ReenrichUnconfirmedError.prototype);
  }
}

/**
 * Thrown by `lockFundsAndStart` when a budgeted, non-cancelled task has no
 * accepted proposal, so funding it would create an escrow that can never pay
 * that task.
 *
 * Carries the ids rather than only a count because the creator's next action
 * is to go and staff those specific tasks, and on a five-task quest a bare
 * "cannot fund" is a scavenger hunt.
 *
 * This is a control, not an affordance. ProposalCard's `allTasksAgreed` check
 * runs in the client on data the client fetched; a second tab, a retry, or a
 * direct service call reaches the money path without it. It matters because
 * `EscrowVault.createEscrow` reverts `EscrowExists` — the total is immutable
 * once set, so a task left unstaffed at this instant can never be funded.
 */
export class UnstaffedTaskError extends Error {
  readonly taskIds: readonly string[];

  constructor(taskIds: string[]) {
    super(`Cannot fund: ${taskIds.length} task(s) have no accepted proposal`);
    this.name = "UnstaffedTaskError";
    this.taskIds = taskIds;
    Object.setPrototypeOf(this, UnstaffedTaskError.prototype);
  }
}

/**
 * Thrown by `reviewSubmission` when approving work would release payment from
 * an escrow a dispute has frozen.
 *
 * The freeze is QUEST-WIDE and cannot be narrowed here.
 * `DisputeResolver.fileDispute` takes a task id but calls
 * `IEscrowVault.freezeEscrow(questId)`, and `EscrowVault.releasePayment`
 * reverts `EscrowFrozen()` for every task underneath it. The protocol is
 * deployed and frozen, so one worker's dispute genuinely does block payment to
 * every other worker on the quest.
 *
 * What this error changes is that the app now SAYS so. Without the preflight
 * the approval was sent, reverted deep inside the vault, and surfaced as an
 * opaque chain failure — the creator paid gas for nothing and the worker saw an
 * approval that did not pay. Carries the quest id because the blocking dispute
 * is, by construction, on a DIFFERENT task than the one being approved, so the
 * message has to point somewhere other than the work in front of the reader.
 */
export class EscrowFrozenError extends Error {
  readonly questId: string;

  constructor(questId: string) {
    super(`Escrow for quest ${questId} is frozen by an open dispute; payment cannot be released`);
    this.name = "EscrowFrozenError";
    this.questId = questId;
    Object.setPrototypeOf(this, EscrowFrozenError.prototype);
  }
}

/**
 * Thrown when the quest's escrow cannot release payment for a reason that is
 * NOT a dispute — it was already fully released, or the creator refunded it.
 *
 * `EscrowVault.releasePayment` accepts only Active and PartiallyReleased and
 * reverts the same `EscrowFrozen()` for every other status, so these fail in
 * exactly the way `EscrowFrozenError` describes while having nothing to do
 * with a dispute. They are a separate error because the remedy is different
 * and the wrong message is actively misleading: telling a creator whose escrow
 * was refunded that "another task is in dispute" sends them hunting for a
 * dispute that does not exist.
 *
 * Carries the raw status so a support report can say which door was shut.
 */
export class EscrowNotReleasableError extends Error {
  readonly questId: string;
  readonly escrowStatus: number;

  constructor(questId: string, escrowStatus: number) {
    super(
      `Escrow for quest ${questId} is in status ${escrowStatus} and can no longer release payment`,
    );
    this.name = "EscrowNotReleasableError";
    this.questId = questId;
    this.escrowStatus = escrowStatus;
    Object.setPrototypeOf(this, EscrowNotReleasableError.prototype);
  }
}

/**
 * Thrown when a quest or task is already on the caller's board (unique
 * index board_cards_owner_quest / board_cards_owner_task, SQLSTATE 23505).
 */
export class AlreadyPinnedError extends Error {
  readonly kind: BoardLinkKind;
  readonly targetId: string;

  constructor(kind: BoardLinkKind, targetId: string) {
    super(`This ${kind} is already on your board`);
    this.name = "AlreadyPinnedError";
    this.kind = kind;
    this.targetId = targetId;
    Object.setPrototypeOf(this, AlreadyPinnedError.prototype);
  }
}
